import { useEffect, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { safeStorage } from '@/lib/storage'
import { THEME_STORAGE_KEY, applyTheme, preferredTheme, storedTheme } from '@/theme'
import type { Theme } from '@/theme'

/**
 * Switches between the two colourways.
 *
 * The token layer keys dark off `[data-theme="dark"]` on the root element (see
 * design/tokens/tailwind.config.ts), so every colour follows from this one
 * attribute and no component needs a dark-mode variant of its own.
 *
 * **Two renderings, one control** (SHELL-18). The bar form is the navy pill the
 * public pages, the wizard and the organisation picker all carry — none of
 * those has a menu to hide it in. The `menuitem` form is what the signed-in top
 * bar uses: `shell.html:97-104` draws that bar as *crumbs · spacer · bell ·
 * avatar* and nothing else, and a theme preference belongs beside *Settings*
 * and *Billing* rather than as a permanent fourth control on every screen.
 * Moving it rather than dropping it, because the control is real and the mockup
 * simply predates it.
 *
 * The menu form states the **action** — *Switch to dark theme* — where the bar
 * form states the current value. In a bar the glyph and the word are a status
 * light you read at a glance; in a list of verbs, *Light* would be the only
 * entry that named a state instead of what pressing it does.
 */
export function ThemeToggle({
  menuitem = false,
  role,
}: {
  menuitem?: boolean
  /**
   * The row form's ARIA role. `menuitem` inside `TopBar`'s menu; omitted in
   * the drawer's account block (parity P-MOB-01, 2026-09-13), which is a plain
   * list of rows and not a `role="menu"` — a `menuitem` outside a menu is an
   * error a screen reader reports rather than a row it can use.
   */
  role?: 'menuitem' | undefined
} = {}): JSX.Element {
  // `safeStorage()` and not `window.localStorage`: this initialiser runs during
  // render, and this button is on the public pages, so an unguarded throw in a
  // storage-blocked browser blanks the sign-in screen (WEB-8).
  const storage = useMemo(() => safeStorage(), [])
  const [theme, setTheme] = useState<Theme>(() =>
    preferredTheme(storage, window.matchMedia('(prefers-color-scheme: dark)').matches),
  )

  // Apply, never store: `bootTheme` (main.tsx) owns the attribute at load and
  // the click below is the one place a choice is written. Storing here on
  // mount is what used to pin a page to the OS theme of its first visit.
  useEffect(() => {
    applyTheme(theme)
  }, [theme])

  // Keep following the OS while no choice has been made, so the label on this
  // button and the attribute `bootTheme` maintains cannot disagree.
  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const follow = (event: MediaQueryListEvent): void => {
      if (storedTheme(storage) === null) setTheme(event.matches ? 'dark' : 'light')
    }
    media.addEventListener('change', follow)
    return () => {
      media.removeEventListener('change', follow)
    }
  }, [storage])

  const choose = (): void => {
    storage.setItem(THEME_STORAGE_KEY, next)
    setTheme(next)
  }

  const next: Theme = theme === 'dark' ? 'light' : 'dark'
  const label = `Switch to ${next} theme`

  if (menuitem) {
    return (
      <button type="button" role={role} onClick={choose}>
        <span aria-hidden="true">{next === 'dark' ? '☾' : '☀'}</span> {label}
      </button>
    )
  }

  return (
    <button
      type="button"
      className="ar-btn ar-btn--secondary ar-btn--sm"
      // WCAG 2.5.3, Label in Name: the visible word must be part of the
      // accessible name, or a voice user who says "Light" gets nothing. So the
      // name is *state, then action* — "Light theme. Switch to dark" — and the
      // tooltip stays the plain action.
      aria-label={`${theme === 'dark' ? 'Dark' : 'Light'} theme. ${label}`}
      // The visible word is the current theme (a status light, see above) and
      // the accessible name is the action. A sighted reader gets only the
      // first, and "Light" on a dark pill reads as either; the tooltip says
      // which.
      title={label}
      onClick={choose}
    >
      <span aria-hidden="true">{theme === 'dark' ? '☾' : '☀'}</span>
      <span>{theme === 'dark' ? 'Dark' : 'Light'}</span>
    </button>
  )
}
